import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useTheme } from '../ThemeContext';

const TeamDashboard = ({ profile }) => {
  const { colors } = useTheme();
  const [teamStats, setTeamStats] = useState({
    totalClients: 0,
    totalVendors: 0,
    teamMembers: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);

  const brandColor = '#59c4c4';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (profile?.team_id) {
      loadTeamStats();
    }
  }, [profile]);

  const loadTeamStats = async () => {
    try {
      const { data: members, error: membersError } = await supabase.rpc('team_member_profiles');
      if (membersError) throw membersError;
      const memberList = members || [];
      const names = new Map(memberList.map((member) => [member.id, member]));
      const memberIds = memberList.map((member) => member.id);

      const [clientsRes, vendorsRes, activityRes] = await Promise.all([
        memberIds.length
          ? supabase
            .from('clients')
            .select('id', { count: 'exact', head: true })
            .in('agent_id', memberIds)
            .eq('is_team_client', true)
          : Promise.resolve({ count: 0 }),

        supabase
          .from('vendors')
          .select('id', { count: 'exact', head: true })
          .eq('team_id', profile.team_id)
          .eq('visibility', 'team'),

        supabase
          .from('team_activity_log')
          .select('*')
          .eq('team_id', profile.team_id)
          .order('created_at', { ascending: false })
          .limit(10)
      ]);

      setTeamStats({
        totalClients: clientsRes.count || 0,
        totalVendors: vendorsRes.count || 0,
        teamMembers: memberList.length,
        recentActivity: (activityRes.data || []).map((activity) => ({
          ...activity,
          agent: names.get(activity.agent_id) || null,
        })),
      });
    } catch (error) {
      console.error('Error loading team stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!profile?.team_id) {
    return null;
  }

  if (loading) {
    return <div style={{padding: '2rem'}}>Loading team dashboard...</div>;
  }

  return (
    <div style={styles.container}>
      <h2 style={{marginBottom: '1.5rem', color: colors.text}}>Team Overview</h2>

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
        <div style={{...styles.statCard, background: colors.surface, border: `1px solid ${colors.border}`}}>
          <div style={{fontSize: '2.5rem', fontWeight: 'bold', color: brandColor}}>
            {teamStats.totalClients}
          </div>
          <div style={{color: colors.textSecondary, marginTop: '0.5rem'}}>Team Clients</div>
        </div>

        <div style={{...styles.statCard, background: colors.surface, border: `1px solid ${colors.border}`}}>
          <div style={{fontSize: '2.5rem', fontWeight: 'bold', color: brandColor}}>
            {teamStats.totalVendors}
          </div>
          <div style={{color: colors.textSecondary, marginTop: '0.5rem'}}>Team Vendors</div>
        </div>

        <div style={{...styles.statCard, background: colors.surface, border: `1px solid ${colors.border}`}}>
          <div style={{fontSize: '2.5rem', fontWeight: 'bold', color: brandColor}}>
            {teamStats.teamMembers}
          </div>
          <div style={{color: colors.textSecondary, marginTop: '0.5rem'}}>Team Members</div>
        </div>
      </div>

      {/* Recent Activity */}
      <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`, marginTop: '1.5rem'}}>
        <h3 style={{marginBottom: '1rem', color: colors.text}}>Recent Team Activity</h3>
        {teamStats.recentActivity.length === 0 ? (
          <p style={{color: colors.textSecondary}}>No recent activity</p>
        ) : (
          <div>
            {teamStats.recentActivity.map(activity => (
              <div key={activity.id} style={{...styles.activityItem, borderBottom: `1px solid ${colors.border}`}}>
                <div style={{fontSize: '0.9rem', color: colors.text}}>
                  {activity.description}
                </div>
                <div style={{fontSize: '0.8rem', color: colors.textSecondary, marginTop: '0.25rem'}}>
                  {activity.agent?.first_name} {activity.agent?.last_name} • {new Date(activity.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '2rem'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem'
  },
  statCard: {
    padding: '1.5rem',
    borderRadius: '12px',
    textAlign: 'center'
  },
  card: {
    padding: '1.5rem',
    borderRadius: '12px'
  },
  activityItem: {
    padding: '1rem 0'
  }
};

export default TeamDashboard;
